/**
 * Advertiser Context - Separate auth context for advertiser portal
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ORIGIN } from '@/lib/config';

const API_URL = API_ORIGIN;

const AdvertiserContext = createContext(null);

export const useAdvertiser = () => {
  const context = useContext(AdvertiserContext);
  if (!context) {
    throw new Error('useAdvertiser must be used within AdvertiserProvider');
  }
  return context;
};

export const AdvertiserProvider = ({ children }) => {
  const [advertiser, setAdvertiser] = useState(null);
  const [team, setTeam] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('advertiser_token'));
  const [loading, setLoading] = useState(true);

  // Fetch current advertiser on mount if token exists
  useEffect(() => {
    const fetchAdvertiser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/advertisers/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAdvertiser(data);
          
          // Fetch wallet
          const walletRes = await fetch(`${API_URL}/api/advertisers/wallet`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (walletRes.ok) {
            const walletData = await walletRes.json();
            setWallet(walletData);
          }
        } else {
          // Token invalid, clear it
          localStorage.removeItem('advertiser_token');
          setToken(null);
        }
      } catch (error) {
        console.error('Error fetching advertiser:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdvertiser();
  }, [token]);

  const login = async (email, password) => {
    // Use a fresh XMLHttpRequest for better error handling
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/advertisers/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        let data;
        try {
          data = JSON.parse(xhr.responseText);
        } catch (e) {
          console.error('Login parse error:', xhr.responseText);
          reject(new Error('Server returned invalid response. Please try again.'));
          return;
        }
        
        if (xhr.status === 500) {
          console.error('Login server error:', data);
          reject(new Error('Server error. Please try again later.'));
          return;
        }
        
        if (xhr.status !== 200) {
          reject(new Error(data.detail || 'Invalid email or password'));
          return;
        }
        
        localStorage.setItem('advertiser_token', data.token);
        setToken(data.token);
        setAdvertiser(data.advertiser);
        setTeam(data.team);
        setWallet(data.wallet);
        resolve(data);
      };
      
      xhr.onerror = function() {
        console.error('Login network error');
        reject(new Error('Network error. Please check your connection.'));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  const register = async (formData) => {
    // Use XMLHttpRequest for better error handling
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/advertisers/register`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        let data;
        try {
          data = JSON.parse(xhr.responseText);
        } catch (e) {
          console.error('Register parse error:', xhr.responseText);
          reject(new Error('Server returned invalid response. Please try again.'));
          return;
        }
        
        if (xhr.status === 500) {
          console.error('Register server error:', data);
          reject(new Error('Server error. Please try again later.'));
          return;
        }
        
        if (xhr.status !== 200 && xhr.status !== 201) {
          reject(new Error(data.detail || 'Registration failed'));
          return;
        }
        
        localStorage.setItem('advertiser_token', data.token);
        setToken(data.token);
        setAdvertiser(data.advertiser);
        setTeam(data.team);
        setWallet(data.wallet);
        resolve(data);
      };
      
      xhr.onerror = function() {
        console.error('Register network error');
        reject(new Error('Network error. Please check your connection.'));
      };
      
      xhr.send(JSON.stringify(formData));
    });
  };

  const logout = useCallback(() => {
    localStorage.removeItem('advertiser_token');
    setToken(null);
    setAdvertiser(null);
    setTeam(null);
    setWallet(null);
  }, []);

  const refreshWallet = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/advertisers/wallet`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const walletData = await response.json();
        setWallet(walletData);
      }
    } catch (error) {
      console.error('Error refreshing wallet:', error);
    }
  };

  const value = {
    advertiser,
    team,
    wallet,
    token,
    loading,
    isAuthenticated: !!advertiser,
    login,
    register,
    logout,
    refreshWallet,
    setAdvertiser,
    setTeam,
    setWallet,
  };

  return (
    <AdvertiserContext.Provider value={value}>
      {children}
    </AdvertiserContext.Provider>
  );
};

export default AdvertiserContext;
